/** @format */

import React, { FC, ReactElement, useCallback, useEffect, useState } from 'react';
import { Dialog, DialogActions, DialogContent, DialogTitle } from '@material-ui/core';
import { ReactComponent as Cancel } from '../../assets/themes/OPL/img/icons/Cancel.svg';
import CustomTextButtom from '../../components/Buttons/CustomTextButton';
import GridContainer from '../../components/Grid/GridContainer';
import GridItem from '../../components/Grid/GridItem';
import I18n from '../../components/Translate/I18n';
import Button from '@material-ui/core/Button';
import CustomInputExt from '../../components/CustomInput/CustomInputExt';
import { ServicesMvneType, ServiceUseTypeMap, useOperatorType, useServiceType, useServiceUseType } from '../../modules/lib/hooks/useOperatorType';
import { SimpleSelect } from '../../components/Adescom/SimpleSelect';
import { ServicesAction } from '../../modules/features/FeaturesReducers';
import { SimCardServiceTypeItem, SimCardServiceTypePost } from '../../interfaces/collections/SimCardServiceInterface';
import { FeatureTypes } from '../../modules/features/FeaturesTypes';
import { useSimCardServiceGroupHeader } from '../../modules/lib/hooks/useSimCardServiceGroupList';
import { useDispatch } from 'react-redux';
import { BmeForm } from './component/opl/SimCardServiceTypeForm/BmeFrom';
import { MappedComponentForm } from './component/opl/SimCardServiceTypeForm/MappedComponentForm';
import { ItemLoaders } from '../../core/loaders/ItemLoaders';
import { FormGeneralHelper } from '../../helpers/FormGeneral';
import { NotificationHelper } from '../../helpers/NotificationHelper';

const _ = require('underscore');

type OplSimCardServiceTypeAddFormType = {
    close: () => void;
    reload: () => void;
    open: boolean;
    serviceGroupList: any;
};

export const SimCardServiceTypeAddForm: FC<OplSimCardServiceTypeAddFormType> = ({ close, reload, open, serviceGroupList }): ReactElement | null => {
    const dispatch = useDispatch();
    const featureOperatorType = new FeatureTypes(process.env);
    const operatorType = useOperatorType(featureOperatorType);
    const serviceTypes = useServiceType(featureOperatorType, ServicesMvneType.SDG);
    const groupList = useSimCardServiceGroupHeader(serviceGroupList);
    const serviceUseType = useServiceUseType();
    const [serviceType, setServiceType] = useState<string | null>(null);

    const initObject = useCallback((): SimCardServiceTypePost => {
        return {
            mainname: null,
            local: false,
            groupId: null,
            description: null,
            serviceType: null,
            comment: null,
            generateCdr: false,
            externalSystemId: null,
            operatorType: 2,
            codeName: null,
            codeName2: null,
            codeNameLevel: null,
            syncDetails: true,
            syncUsage: true,
            visibleFlag: true,
            singleShotFlag: false,
            cycle: 0,
            cdrOnAmount: 0,
            configuration: '{}',
            useType: ServiceUseTypeMap.BOTH,
        };
    }, []);

    const [simCardServiceTypeStack, setSimCardServiceTypeStack] = useState(initObject());
    useEffect(() => {
        if (simCardServiceTypeStack === null) {
            setSimCardServiceTypeStack(initObject());
        }
    }, [simCardServiceTypeStack, initObject]);

    const onChangeSelectEvent = (event: React.ChangeEvent<HTMLSelectElement>): void => {
        const current = _.clone(simCardServiceTypeStack);
        const field = event.target.name;
        const selected = event.target.value;
        switch (field) {
            case 'visibleFlag':
            case 'syncDetails':
            case 'mccCheck':
            case 'allowWaitingForActivation':
            case 'zoneCheck':
            case 'syncUsage':
            case 'singleShotFlag':
            case 'generateCdr':
                setSimCardServiceTypeStack({ ...current, [field]: parseInt(selected) === 1 });
                break;
            case 'serviceType':
                setServiceType(featureOperatorType.mapToString(parseInt(selected)));
                setSimCardServiceTypeStack({ ...current, [field]: featureOperatorType.mapToString(parseInt(selected)) });
                break;
            case 'cycle':
            case 'cdrOnAmount':
                setSimCardServiceTypeStack({ ...current, [field]: parseInt(selected) === 1 ? 1 : -1 });
                break;
            default:
                setSimCardServiceTypeStack({ ...current, [field]: selected });
                break;
        }
    };

    const onChangeEvent = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const current = _.clone(simCardServiceTypeStack);
        const value = event.target.value;
        const field = event.target.name;
        if (field !== 'externalSystemId') {
            setSimCardServiceTypeStack({ ...current, [field]: value });
        } else {
            setSimCardServiceTypeStack({ ...current, externalSystemId: parseInt(value ?? -1) });
        }
    };

    const getServiceTypeId = (serviceTypeName: string): number => {
        let id = -2;
        for (let i = 0; i < serviceTypes.length; i++) {
            if (serviceTypeName === serviceTypes[i].name) {
                return serviceTypes[i].value;
            }
        }
        return id;
    };

    const closeCallback = (): void => {
        setServiceType(null);
        setSimCardServiceTypeStack(initObject());
        close();
    };

    const confirmCallback = (): void => {
        const itemLoader = new ItemLoaders(dispatch);
        itemLoader.postWithCallback(
            FormGeneralHelper.getByUri('/adescom-api/api/platform/sim_card_service_types'),
            'no-dispatch',
            {
                ...simCardServiceTypeStack,
                codeName: simCardServiceTypeStack.mainname,
                codeName2: simCardServiceTypeStack.mainname,
                codeNameLevel: simCardServiceTypeStack.mainname,
                externalSystemId: simCardServiceTypeStack.serviceType === ServicesMvneType.MCBME ? -1 : simCardServiceTypeStack.externalSystemId,
            } as SimCardServiceTypePost,
            {
                onSuccess: (data: SimCardServiceTypeItem): void => {
                    reload();
                    closeCallback();
                },
                onError: (data: any): void => {
                    NotificationHelper.notifyError('prov:feature.fill_all_required_fields', dispatch);
                },
            },
            { propagateDataToCallback: true, dispatchAfterCallback: false }
        );
    };

    return (
        <Dialog
            open={open}
            maxWidth="sm"
            fullWidth={true}
            onClose={closeCallback}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
        >
            <DialogTitle>
                <GridContainer justifyContent={'space-between'}>
                    <GridItem xs={11}>
                        <h4>
                            <I18n trans="prov:feature.add_new_feature" />
                        </h4>
                    </GridItem>
                    <GridItem xs={1}>
                        <Button onClick={closeCallback} color="primary">
                            <Cancel width={24} height={24} />
                        </Button>
                    </GridItem>
                </GridContainer>
            </DialogTitle>
            <DialogContent>
                <GridContainer spacing={1}>
                    <GridItem xs={6}>
                        <CustomInputExt
                            formControlProps={{ fullWidth: true }}
                            required={true}
                            labelText={'prov:feature.component_id'}
                            name="mainname"
                            value={simCardServiceTypeStack.mainname}
                            onChange={onChangeEvent}
                        />
                    </GridItem>
                    <GridItem xs={6} style={{ paddingTop: '8px' }}>
                        <SimpleSelect
                            label="prov:feature.service_type"
                            fieldName="serviceType"
                            required={true}
                            elements={serviceTypes}
                            dispatchName={ServicesAction.FEATURES_LOAD_SIM_CARD_SERVICE_TYPE_EDIT_UPDATE_SELECT}
                            selectedValue={getServiceTypeId(simCardServiceTypeStack.serviceType ?? 'empty')}
                            callback={onChangeSelectEvent}
                        />
                    </GridItem>
                    {serviceType === ServicesMvneType.BME && (
                        <BmeForm
                            serviceUseType={serviceUseType}
                            simCardServiceTypeStack={simCardServiceTypeStack}
                            onChangeSelectEvent={onChangeSelectEvent}
                            onChangeEvent={onChangeEvent}
                            operatorType={operatorType}
                            groupList={groupList}
                        />
                    )}
                    {serviceType === ServicesMvneType.MCBME && (
                        <MappedComponentForm
                            serviceUseType={serviceUseType}
                            simCardServiceTypeStack={simCardServiceTypeStack}
                            onChangeSelectEvent={onChangeSelectEvent}
                            onChangeEvent={onChangeEvent}
                            operatorType={operatorType}
                            groupList={groupList}
                        />
                    )}
                </GridContainer>
            </DialogContent>
            <DialogActions>
                <CustomTextButtom callback={closeCallback} callbackParam={{}} label="general:common.cancel" size="sm" />
                {simCardServiceTypeStack.mainname && simCardServiceTypeStack.serviceType && (
                    <CustomTextButtom callback={confirmCallback} callbackParam={{}} label="prov:feature.add_new" size="sm" />
                )}
            </DialogActions>
        </Dialog>
    );
};
